import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SummaryBarContainer } from '@bingads-webui-react/summary-bar';
import { ActionButton } from '@bingads-webui-react/fabric-control';
import { getSessionStorage } from '@bingads-webui/storage';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import { isNavigationRefreshOn } from '@bingads-webui-react/preference-utility';
import { getAssetLibrarySummaryBarStyle } from './styles/asset-library-summary-bar-style';

const SessionStorage = getSessionStorage();
const SESSION_KEY = 'ASSET_LIBRARY_STORAGE';
const QUERY_INTERVAL = 1 * 1000;
const MAX_QUERY_COUNT = (30 * 1000) / QUERY_INTERVAL; // query few more seconds after count is 0

const AssetLibrarySummaryBarInternal = ({
  selectedParent,
  selectedChild,
  location,
  permissions,
  i18n,
  classes,
} = {}) => {
  //
  const [inProgressTaskCount, setInProgressTaskCount] = useState(0);

  // query in progress tasks count
  useEffect(() => {
    if (!selectedChild || selectedChild.id !== 'creative-studio-asset-library') {
      return null;
    }
    let counter = 0;
    let timerId = setInterval(() => {
      let { inProgressTaskCount: val } = SessionStorage.get(SESSION_KEY) || {};
      val = val || 0;
      setInProgressTaskCount(val);

      // stop query after task count becomes 0 for a while
      counter = val > 0 ? 0 : counter + 1;
      if (!val && counter > MAX_QUERY_COUNT) {
        clearInterval(timerId);
        timerId = undefined;
      }
    }, QUERY_INTERVAL);
    return () => {
      if (timerId !== undefined) {
        clearInterval(timerId);
      }
    };
  }, [selectedChild]);

  const backButtonProps = useMemo(() => {
    if (location && selectedParent && selectedChild
        && selectedChild.id === 'creative-studio-asset-library-image-edit') {
      return {
        url: `${selectedParent.routePrefix}${selectedParent.route}${location.search}`,
        buttonText: i18n.getString(_TL_('Back')),
      };
    }
    return null;
  }, [i18n, location, selectedChild, selectedParent]);

  const renderInProgressContent = useCallback(() => {
    // only show when there are in progress tasks
    if (inProgressTaskCount) {
      return (
        <div className={classNames('summary-bar-items', classes.inProgressContent)}>
          <ActionButton
            className="in-progress-button"
            iconProps={{ iconName: 'Sync' }}
            onClick={() => {
              // history.push(`${selectedParent.routePrefix}assetlibrary/taskprogress${location.search}`);
            }}
          >
            {inProgressTaskCount > 1
              ? i18n.getString(_TL_('Updating {{count}} items'), { count: inProgressTaskCount })
              : i18n.getString(_TL_('Updating 1 item'))}
          </ActionButton>
        </div>
      );
    }
    return null;
  }, [classes, i18n, inProgressTaskCount]);

  const summaryBar = useMemo(() => (
    <div className={classes.assetLibrarySummaryBar}>
      <div id="asset-library-summary-bar-breadcrumb" className="breadcrumb-wrapper">
        {/* render breadcrumb here using React.createPortal */}
      </div>
      {
        renderInProgressContent()
      }
    </div>
  ), [classes, renderInProgressContent]);

  const ariaLabel = useMemo(() => ({
    ariaLabel: i18n.getString(_TL_('Summary bar')),
  }), [i18n]);

  return selectedParent && selectedChild ? (
    <SummaryBarContainer
      texts={ariaLabel}
      backButtonProps={backButtonProps}
      breadcrumb={null}
      summaryBar={summaryBar}
      showScopeBackButton={isNavigationRefreshOn(permissions)}
      i18n={i18n}
    />
  ) : null;
};

AssetLibrarySummaryBarInternal.propTypes = {
  classes: PropTypes.shape({
    assetLibrarySummaryBar: PropTypes.string,
    inProgressContent: PropTypes.string,
  }).isRequired,
  selectedParent: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  selectedChild: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  history: PropTypes.shape({
    replace: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      NavigationRefresh: PropTypes.bool,
    }),
  }).isRequired,
};

AssetLibrarySummaryBarInternal.defaultProps = {
};

export const AssetLibrarySummaryBar = withPermissions(withDefaultStyles(
  AssetLibrarySummaryBarInternal,
  getAssetLibrarySummaryBarStyle
));
